import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CamelCasePipe } from '../../../utils/camel-case.pipe';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FieldSelectedValue } from '../../../model/form.model';
import { CommonUtils } from '../../../utils/common.utils';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-mat-file-upload',
  standalone: true,
  imports: [CommonModule, MatFormFieldModule, CamelCasePipe, ReactiveFormsModule, FormsModule, MatIconModule],
  templateUrl: './mat-file-upload.component.html',
  styleUrl: './mat-file-upload.component.scss'
})
export class MatFileUploadComponent implements OnInit ,OnChanges {
  @Input() field!: any;
  @Input() form!: FormGroup;
  @Output() fileSelected = new EventEmitter<FieldSelectedValue>();

  public attachmentFiles: Record<string, File> = {}
  private readonly commonUtils = inject(CommonUtils)

  ngOnInit() {
    const fieldName = this.field?.['fieldName'];
    const control = fieldName && this.form.get(fieldName);
    if (control) {
      this.form.get(fieldName)?.valueChanges.subscribe(() => {
        if(this.field.selectedValue === ''){
          this.attachmentFiles = {}
        }
      });
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    const field = changes['field']?.currentValue?.selectedValue;
    const prevField = changes['field']?.previousValue?.selectedValue;

    if (field !== prevField && [null, undefined, ''].includes(field)) {
      this.attachmentFiles = {}
    }
  }

  async onFileChange(fieldName: string, event: any): Promise<void> {
    const inputFiles: FileList | null = (event.target as HTMLInputElement)?.files;
    if (!inputFiles?.length || !this.field) {return};

    let overrideCount = 0;
    let invalidTypeCount = 0;
    Array.from(inputFiles ?? []).forEach((file: File) => {
      const validation: 'valid' | 'invalid' = this.validate(file);
      if (validation === 'valid') {
        if (this.attachmentFiles[file['name']]) {
          overrideCount++;
        }
        this.attachmentFiles[file['name']] = file;
      } else if (validation === 'invalid') {
        invalidTypeCount++;
      }
    })
    event.target.value = '';
    const messages: string[] = [];

    if (overrideCount === 1) {
      messages.push('File was overridden.');
    } else if (overrideCount > 1) {
      messages.push(`${overrideCount} files were overridden.`);
    }
    if (invalidTypeCount === 1) {
      messages.push('Invalid file type was skipped.');
    } else if (invalidTypeCount > 1) {
      messages.push(`${invalidTypeCount} invalid file types were skipped.`);
    }

    if (messages.length > 0) {
      this.commonUtils.showMatSnackBar(messages.join(' '));
    } try {
      this.field['selectedValue'] = Object.values(this.attachmentFiles)
      this.fileSelected.emit({ FieldName: fieldName, Value: Object.values(this.attachmentFiles) });
    } catch (error) {
      console.info('File processing error:', error);
      this.commonUtils.showMatSnackBar('Error processing file(s).');
    }
  }
  validate(file: File): 'valid' | 'invalid' {
    const allowedTypes = this.field?.fileTypes;
    const restrictedFileTypes = this.field?.restrictedFileTypes;
    if (allowedTypes?.length > 0 && !allowedTypes.includes(file.type)) {
      return 'invalid';
    }
    if (restrictedFileTypes?.length > 0 && restrictedFileTypes.includes(file.type)) {
      return 'invalid';
    }
    return 'valid';
  }

  async formatToBase64(files: File[]): Promise<string[]> {
    return Promise.all(files.map(file => this.commonUtils.readFileAsBase64(file)));
  }

  clear(fileName: string) {
    if (this.attachmentFiles[fileName]) {
      delete this.attachmentFiles[fileName];
    }
  }
}
